import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { DropletsIcon, WindIcon, CarIcon, TractorIcon, PaintbrushIcon, AnchorIcon, BrickWallIcon, LayersIcon, TrainIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ServicesHubProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ServicesHub({
  setPage
}: ServicesHubProps) {
  const services = [{
    icon: DropletsIcon,
    title: 'Dustless Blasting',
    desc: 'Low-mess wet blasting that helps suppress airborne dust while stripping coatings and rust. A good fit for urban environments and sensitive substrates.',
    link: 'services' // Maps to the ServicePageTemplate
  }, {
    icon: WindIcon,
    title: 'Dry Blasting',
    desc: 'Traditional abrasive blasting for heavy-duty coating removal, severe rust, and aggressive surface preparation on robust industrial materials.',
    link: 'contact'
  }, {
    icon: CarIcon,
    title: 'Automotive Restoration',
    desc: 'Strip paint, rust, and body filler from cars, trucks, and classic vehicles down to bare metal without warping thin sheet metal panels.',
    link: 'contact'
  }, {
    icon: TractorIcon,
    title: 'Heavy Equipment Cleaning',
    desc: 'Remove rust, industrial paint, and heavy grease buildup from construction, farm, and industrial machinery to prepare for recoating.',
    link: 'contact'
  }, {
    icon: PaintbrushIcon,
    title: 'Graffiti Removal',
    desc: 'Controlled surface prep to take graffiti off brick, concrete, and stone, with media and pressure matched to the substrate.',
    link: 'contact'
  }, {
    icon: AnchorIcon,
    title: 'Boat & Marine Prep',
    desc: 'Strip bottom paint, anti-fouling coatings, and gelcoat blisters for marine refinishing and fiberglass restoration.',
    link: 'contact'
  }, {
    icon: BrickWallIcon,
    title: 'Concrete & Brick Restoration',
    desc: 'Clean, etch, and restore concrete and masonry surfaces for commercial buildings, historic restorations, and residential projects.',
    link: 'contact'
  }, {
    icon: LayersIcon,
    title: 'Powder Coating Removal',
    desc: 'Efficiently strip tough, baked-on powder coating from metal parts, frames, and industrial equipment.',
    link: 'contact'
  }, {
    icon: TrainIcon,
    title: 'Rail Car Blasting',
    desc: 'Blast and prepare rail cars, tankers, and rolling stock for repainting, maintenance, and compliance inspections.',
    link: 'contact'
  }];
  return <div className="w-full bg-white" data-id="element-756" data-mp="37i73ci4puy67">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-757" data-mp="2zwboqvwskw23">
        <div className="max-w-7xl mx-auto text-center" data-id="element-758" data-mp="2zyxy9138tlmy">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-759" data-mp="2ux8ij852sjsj">
            Our <span className="text-brand-red" data-id="element-760" data-mp="34udajollr9h9">Services</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed" data-id="element-761" data-mp="2h6wsrfvyqx4k">
            Professional mobile surface preparation across Michigan. We utilize
            both dustless and dry blasting techniques to deliver the perfect
            finish for any project.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-steel-50" data-id="element-762" data-mp="1tgsnl434mpbg">
        <div className="max-w-7xl mx-auto px-4" data-id="element-763" data-mp="wq7bh5vtt8h3">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-764" data-mp="wridp19mi863">
            {services.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 border border-steel-200 hover:border-brand-red hover:shadow-xl transition-all duration-300 group flex flex-col h-full" data-id="element-765" data-mp="1fgyfdrx1o6o3" data-mpi={idx}>
                <div className="w-14 h-14 bg-steel-50 rounded-full flex items-center justify-center mb-6 border border-steel-100 group-hover:bg-brand-red/10 group-hover:border-brand-red/30 transition-colors" data-id="element-766" data-mp="1of0lglvmxpi8">
                  <service.icon className="w-7 h-7 text-brand-red group-hover:scale-110 transition-transform duration-300" data-id="element-767" data-mp="3v71hdwlu0e8i" />
                </div>
                <h3 className="text-2xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-768" data-mp="3nok3zfau4fa9">
                  {service.title}
                </h3>
                <p className="text-steel-600 mb-8 text-base leading-relaxed flex-grow" data-id="element-769" data-mp="3dxyyd7wquahu">
                  {service.desc}
                </p>
                <button onClick={() => setPage(service.link)} className="inline-flex items-center text-brand-red font-bold text-sm uppercase tracking-wider hover:text-brand-red-dark mt-auto self-start" data-id="element-770" data-mp="38zxhmtmsin9j">
                  Learn More{' '}
                  <span className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-771" data-mp="i195p7qx8l4">
                    →
                  </span>
                </button>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-charcoal text-white text-center px-4" data-id="element-772" data-mp="2h7ydvl3eqg6c">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-773" data-mp="2ha2n8ik2s7e0">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-774" data-mp="28qxyf3joq8u6">
            Not Sure What You Need?
          </h2>
          <p className="text-xl font-body mb-10 text-steel-300" data-id="element-775" data-mp="2dohgkeji75re">
            Every project is unique. Contact us to discuss your specific surface
            preparation requirements and we'll recommend the best approach.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-776" data-mp="24qxckxu9avd3">
            <Button variant="primary" size="lg" onClick={() => setPage('contact')} showPhoneIcon className="w-full sm:w-auto" data-id="element-777" data-mp="1py3kmcxssmrj">
              Call (517) 777-8088
            </Button>
            <Button variant="outline" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto" data-id="element-778" data-mp="1kztn3volpa2r">
              Request a Quote
            </Button>
          </div>
        </motion.div>
      </section>
    </div>;
}